import { FormEvent, useCallback, useEffect, useState } from 'react';
import { PageHeader, StatCard } from '../components/UI';
import {
  createTeacherClass,
  createTeacherQuizAssignment,
  listTeacherAssignableQuizzes,
  listTeacherClassAssignments,
  listTeacherClassStudents,
  listTeacherPortalClasses,
  loadTeacherClassSummary,
} from '../lib/teacherPortalRepository';
import type {
  TeacherAssignableQuiz,
  TeacherClassAssignment,
  TeacherClassStudent,
  TeacherClassSummary,
  TeacherPortalClass,
} from '../lib/teacherPortalRepository';
import { useAuth } from '../state/AuthContext';

const levels = ['PG', 'NUR', 'KG', 'C1', 'C2', 'C3', 'C4', 'C5'];

function defaultDueDate() {
  const due = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  due.setMinutes(due.getMinutes() - due.getTimezoneOffset());
  return due.toISOString().slice(0, 16);
}

function TeacherPortalWorkspace({ view }: { view: 'dashboard' | 'classes' | 'assignments' | 'reports' }) {
  const { user } = useAuth();
  const [classes, setClasses] = useState<TeacherPortalClass[]>([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [summary, setSummary] = useState<TeacherClassSummary | null>(null);
  const [students, setStudents] = useState<TeacherClassStudent[]>([]);
  const [quizzes, setQuizzes] = useState<TeacherAssignableQuiz[]>([]);
  const [assignments, setAssignments] = useState<TeacherClassAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [className, setClassName] = useState('');
  const [levelCode, setLevelCode] = useState('C1');
  const [academicYear, setAcademicYear] = useState('2026-2027');
  const [quizId, setQuizId] = useState('');
  const [dueAt, setDueAt] = useState(defaultDueDate);
  const [attemptLimit, setAttemptLimit] = useState(1);

  const refreshClasses = useCallback(async (preferredClassId?: string) => {
    const rows = await listTeacherPortalClasses();
    setClasses(rows);
    setSelectedClassId((current) => preferredClassId || current || rows[0]?.classId || '');
    return rows;
  }, []);

  const refreshSelectedClass = useCallback(async (classId: string) => {
    const [summaryRow, studentRows, quizRows, assignmentRows] = await Promise.all([
      loadTeacherClassSummary(classId),
      listTeacherClassStudents(classId),
      listTeacherAssignableQuizzes(classId),
      listTeacherClassAssignments(classId),
    ]);
    setSummary(summaryRow);
    setStudents(studentRows);
    setQuizzes(quizRows);
    setAssignments(assignmentRows);
    setQuizId((current) => quizRows.some((quiz) => quiz.quizId === current) ? current : quizRows[0]?.quizId || '');
  }, []);

  useEffect(() => {
    setLoading(true);
    refreshClasses().catch((error: Error) => setMessage(error.message)).finally(() => setLoading(false));
  }, [refreshClasses]);

  useEffect(() => {
    if (!selectedClassId) {
      setSummary(null); setStudents([]); setQuizzes([]); setAssignments([]); return;
    }
    setLoading(true);
    refreshSelectedClass(selectedClassId)
      .catch((error: Error) => setMessage(error.message))
      .finally(() => setLoading(false));
  }, [refreshSelectedClass, selectedClassId]);

  async function submitClass(event: FormEvent) {
    event.preventDefault(); setSaving(true); setMessage('');
    try {
      const classId = await createTeacherClass(className, levelCode, academicYear);
      await refreshClasses(classId);
      setClassName('');
      setMessage('Class created securely. Share only its generated join code with the intended parents.');
    } catch (error) { setMessage((error as Error).message); }
    finally { setSaving(false); }
  }

  async function submitAssignment(event: FormEvent) {
    event.preventDefault();
    if (!selectedClassId || !quizId) return;
    setSaving(true); setMessage('');
    try {
      await createTeacherQuizAssignment(selectedClassId, quizId, new Date(dueAt).toISOString(), attemptLimit, 600);
      await refreshSelectedClass(selectedClassId);
      await refreshClasses(selectedClassId);
      setMessage('Assignment created securely. Answers remain private.');
    } catch (error) { setMessage((error as Error).message); }
    finally { setSaving(false); }
  }

  const title = view === 'classes' ? 'Classes' : view === 'assignments' ? 'Assignments' : view === 'reports' ? 'Class reports' : 'Teacher dashboard';
  const description = view === 'classes'
    ? 'Create protected classes and share generated join codes with the correct parents.'
    : view === 'assignments'
      ? 'Assign only published, level-matched quizzes while private answers stay in Supabase.'
      : view === 'reports'
        ? 'Review safe completion and learning summaries without exposing answer keys.'
        : 'Manage classes, assignments and learner progress from one secure workspace.';

  return (
    <section className="portal-page teacher-portal-page">
      <PageHeader title={title} description={description} actions={<span className="pill green">{user?.role === 'super_admin' ? 'Staging oversight' : 'Approved teacher access'}</span>} />
      {message && <p className={`quiz-message ${message.includes('securely') || message.includes('private') ? 'answer-feedback correct' : 'answer-feedback incorrect'}`}>{message}</p>}

      {classes.length > 0 && (
        <div className="panel teacher-class-selector">
          <label>Viewing class<select value={selectedClassId} onChange={(event) => setSelectedClassId(event.target.value)}>{classes.map((item) => <option key={item.classId} value={item.classId}>{item.className} · {item.levelName}</option>)}</select></label>
          <span>Join code <strong>{summary?.joinCode ?? classes.find((item) => item.classId === selectedClassId)?.joinCode}</strong></span>
        </div>
      )}

      {loading && <p className="quiz-busy">Loading the protected teacher workspace…</p>}

      {!loading && classes.length === 0 && view !== 'classes' && (
        <div className="empty-state"><img src="/brand/piko-parrot-mascot.png" alt="Piko parrot mascot" /><h3>No active class yet</h3><p>Create the first class to activate assignments and reports.</p></div>
      )}

      {summary && view === 'dashboard' && (
        <>
          <div className="stats-grid teacher-summary-stats">
            <StatCard label="Active classes" value={classes.length} note="Protected teacher workspace" />
            <StatCard label="Students" value={summary.studentCount} note="Active enrolments" tone="green" />
            <StatCard label="Open assignments" value={summary.openAssignments} note="Scheduled or available" tone="amber" />
            <StatCard label="Average accuracy" value={`${summary.averageAccuracy}%`} note={`${summary.studentsNeedingSupport} may need support`} tone="purple" />
          </div>
          <div className="teacher-dashboard-grid">
            <article className="panel teacher-overview"><p className="eyebrow">Class progress</p><h2>{summary.className}</h2><progress max="100" value={summary.averageAccuracy} /><span>{summary.completedAttempts} completed quiz attempts</span><small>Last activity: {summary.lastActivityAt ? new Date(summary.lastActivityAt).toLocaleString() : 'No completed class activity yet'}</small></article>
            <article className="panel teacher-overview"><p className="eyebrow">Parent enrolment</p><h2>{summary.joinCode}</h2><p>Parents enter this code for a linked, level-matched child. No child email is required.</p></article>
          </div>
        </>
      )}

      {view === 'classes' && (
        <div className="teacher-work-grid">
          <form className="panel teacher-form" onSubmit={submitClass}>
            <p className="eyebrow">Protected class setup</p><h2>Create a class</h2>
            <label>Class name<input value={className} onChange={(event) => setClassName(event.target.value)} maxLength={80} required /></label>
            <label>Level<select value={levelCode} onChange={(event) => setLevelCode(event.target.value)}>{levels.map((level) => <option key={level} value={level}>{level}</option>)}</select></label>
            <label>Academic year<input value={academicYear} onChange={(event) => setAcademicYear(event.target.value)} maxLength={20} required /></label>
            <button className="button" disabled={saving}>{saving ? 'Creating securely…' : 'Create class'}</button>
            <small>Normal teacher accounts require Super Admin approval before class creation.</small>
          </form>
          <article className="panel teacher-roster"><p className="eyebrow">Active roster</p><h2>{summary?.className ?? 'Select a class'}</h2>{students.length === 0 ? <p>No enrolled learners yet. Share the join code with the intended parent.</p> : <div className="teacher-student-list">{students.map((student) => <div key={student.childId}><img src="/brand/piko-parrot-mascot.png" alt="" /><span><strong>{student.displayName}</strong><small>{student.levelCode} · {student.completedAttempts} completed attempts</small></span><em>{student.quizAccuracyPercent}%</em></div>)}</div>}</article>
        </div>
      )}

      {view === 'assignments' && summary && (
        <div className="teacher-work-grid">
          <form className="panel teacher-form" onSubmit={submitAssignment}>
            <p className="eyebrow">Private-answer workflow</p><h2>Create an assignment</h2>
            <label>Published quiz<select value={quizId} onChange={(event) => setQuizId(event.target.value)} required>{quizzes.length === 0 && <option value="">No assignable quiz available</option>}{quizzes.map((quiz) => <option key={quiz.quizId} value={quiz.quizId}>{quiz.title} · {quiz.questionCount} questions</option>)}</select></label>
            <label>Due date<input type="datetime-local" value={dueAt} onChange={(event) => setDueAt(event.target.value)} required /></label>
            <label>Attempt limit<select value={attemptLimit} onChange={(event) => setAttemptLimit(Number(event.target.value))}>{[1, 2, 3, 5].map((limit) => <option key={limit} value={limit}>{limit}</option>)}</select></label>
            <button className="button" disabled={saving || !quizId}>{saving ? 'Saving securely…' : 'Assign quiz'}</button>
            <small>Feedback is released only after submission. Private answer keys never enter the browser.</small>
          </form>
          <div className="teacher-assignment-list">{assignments.map((assignment) => <article className="panel" key={assignment.assignmentId}><div><span className="pill purple">{assignment.status}</span><h2>{assignment.quizTitle}</h2><p>Due {assignment.dueAt ? new Date(assignment.dueAt).toLocaleString() : 'without a deadline'}</p></div><strong>{assignment.completedAttempts}/{assignment.studentCount}</strong><small>completed</small></article>)}</div>
        </div>
      )}

      {view === 'reports' && summary && (
        <>
          <div className="stats-grid teacher-summary-stats"><StatCard label="Students" value={summary.studentCount} note="Active roster" /><StatCard label="Completed attempts" value={summary.completedAttempts} note="Class assignments" tone="green" /><StatCard label="Average accuracy" value={`${summary.averageAccuracy}%`} note="Quiz results" tone="purple" /><StatCard label="Support watch" value={summary.studentsNeedingSupport} note="Below 60% quiz accuracy" tone="amber" /></div>
          <div className="table-wrap"><table><thead><tr><th>Learner</th><th>Quiz attempts</th><th>Quiz</th><th>Math</th><th>Words</th><th>Last activity</th></tr></thead><tbody>{students.map((student) => <tr key={student.childId}><td><strong>{student.displayName}</strong><span className="cell-subtext">{student.levelCode}</span></td><td>{student.completedAttempts}</td><td>{student.quizAccuracyPercent}%</td><td>{student.mathAccuracyPercent}%</td><td>{student.wordAccuracyPercent}%</td><td>{student.lastActivityAt ? new Date(student.lastActivityAt).toLocaleDateString() : '—'}</td></tr>)}</tbody></table></div>
        </>
      )}
    </section>
  );
}

export function TeacherDashboard() { return <TeacherPortalWorkspace view="dashboard" />; }
export function TeacherClassesPage() { return <TeacherPortalWorkspace view="classes" />; }
export function TeacherAssignmentsPage() { return <TeacherPortalWorkspace view="assignments" />; }
export function TeacherReportsPage() { return <TeacherPortalWorkspace view="reports" />; }
